import React, { useEffect, useMemo, useState } from 'react';
import { Container, Box, Button } from '@material-ui/core';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'react-simple-snackbar';
import styles from '../Clients/Clients.module.css';
import AddContact from './AddContact';
import { getContactsByUser, deleteContact } from '../../actions/contactActions';
import { getAccountsByUser } from '../../actions/accountActions';
import { getLocationsByUser } from '../../actions/locationActions';
import useAuthUser from '../../hooks/useAuthUser';

const Contacts = () => {
  const auth = useAuthUser()
  const dispatch = useDispatch()
  const { contacts, isLoading } = useSelector((state) => state.contacts)
  const accounts = useSelector((state) => state.accounts.accounts || [])
  const locations = useSelector((state) => state.locations.locations || [])
  const [open, setOpen] = useState(false)
  const [currentId, setCurrentId] = useState(null)
  // eslint-disable-next-line no-unused-vars
  const [openSnackbar, closeSnackbar] = useSnackbar()

  useEffect(() => {
    const userId = auth?.result?._id || auth?.result?.googleId
    if (userId) {
      dispatch(getContactsByUser({ search: userId }))
      if (!accounts.length) dispatch(getAccountsByUser({ search: userId }))
      if (!locations.length) dispatch(getLocationsByUser({ search: userId }))
    }
  }, [auth, dispatch])

  const accountNameById = useMemo(() => {
    const map = {}
    accounts.forEach(a => { map[a._id] = a.name })
    return map
  }, [accounts])

  const locationNameById = useMemo(() => {
    const map = {}
    locations.forEach(l => { map[l._id] = l.name })
    return map
  }, [locations])

  const locationIdsForContact = (contact) => {
    if (!contact) return []
    if (Array.isArray(contact.locationIds) && contact.locationIds.length) return contact.locationIds
    return contact.locationId ? [contact.locationId] : []
  }

  const rows = useMemo(() => {
    return [...(contacts || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }))
  }, [contacts])

  const handleEdit = (id) => {
    setCurrentId(id)
    setOpen(true)
  }

  const handleDelete = (id) => {
    dispatch(deleteContact(id, openSnackbar))
  }

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1.2, minWidth: 180 },
    { field: 'role', headerName: 'Role', flex: 0.8, minWidth: 140 },
    { field: 'email', headerName: 'Email', flex: 1.1, minWidth: 180 },
    { field: 'phone', headerName: 'Phone', flex: 0.9, minWidth: 140 },
    {
      field: 'accountId',
      headerName: 'Account',
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => accountNameById[params.value] || '',
    },
    {
      field: 'locationIds',
      headerName: 'Locations',
      flex: 1.2,
      minWidth: 200,
      valueGetter: (params) => {
        const names = locationIdsForContact(params.row).map((id) => locationNameById[id]).filter(Boolean)
        return names.join(', ')
      },
    },
    {
      field: 'primary',
      headerName: 'Primary',
      flex: 0.5,
      minWidth: 100,
      renderCell: (params) => (
        <span style={{ color: params.value ? 'green' : 'gray', fontWeight: 600 }}>
          {params.value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      width: 180,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Button
            style={{color: 'white', backgroundColor: 'rgb(25, 118, 210)', marginRight: 10}}
            onClick={(e) => { e.stopPropagation(); handleEdit(params.row._id); }}
            variant="contained"
            size="small"
          >
            Edit
          </Button>
          <Button
            style={{color: 'white', backgroundColor: 'red'}}
            onClick={(e) => { e.stopPropagation(); handleDelete(params.row._id); }}
            variant="contained"
            size="small"
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <div className={styles.pageLayout}>
      <Container style={{ width: '90%', maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <h1>Contacts</h1>
          <button
            className={styles.btn}
            onClick={() => { setCurrentId(null); setOpen((prev) => !prev) }}
          >
            Add Contact
          </button>
        </div>
        <DataGrid
          autoHeight
          loading={isLoading}
          rows={rows}
          getRowId={(row) => row._id}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 300 } } }}
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '6px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
          }}
          onRowDoubleClick={(params) => handleEdit(params.id)}
        />
      </Container>
      <AddContact open={open} setOpen={setOpen} currentId={currentId} setCurrentId={setCurrentId} />
    </div>
  );
};

export default Contacts;
